import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Feature, Geometry } from "geojson";
import { GeoJSON, LatLngBounds, LatLngExpression } from "leaflet";

import { getUserId } from "../../../authentication/store/auth.selector";
import { logoutSuccess } from "../../../authentication/store/auth.slice";
import { isArcGisError } from "../../../shared/services/error";
import { RootState } from "../../../store-types";
import { TArcgisMapCropPropsDto } from "../shared/dtos/arcgis-map-crop-props.dto";
import { IMapPageState } from "../shared/interfaces/map-page-state";

import { MAP_PAGE_MODULE_NAME } from "./constants";
import { mapPageApi } from "./map-page.rtkq";

const NO_GEOJSON_REQUESTED = "INITIAL_STATE:NO_GEOJSON_REQUESTED_YET";
const ARCGIS_TOKEN_ERROR = "WAIT_UNTIL_FarmLandCropsGeoService_ABSORBS_ARCGIS_TOKEN";
const geoJsonKey = ({ farmId, seasonId }: { farmId: string; seasonId: string }): string =>
  `farmId:[${farmId}]:seasonId[${seasonId}]`;
const loadingKey = (requestId: string): string => `LOADING_NOW:${requestId}`;

const initialMapPageState: IMapPageState = {
  geoJson: new GeoJSON<TArcgisMapCropPropsDto>(),
  geoJsonAlreadyFetchedForFarmSeason: NO_GEOJSON_REQUESTED,
  farmLandData: [],
  farmFeatures: undefined, // NOT_YET_LOADED => undefined
  isLoadingGeoJson: false,
  isLoading: false,
  center: [55, 46],
  zoom: 10,
  bounds: new LatLngBounds([
    [54, 84],
    [53, 83],
  ]),
  selectedCropNames: [],
  isDrawerOpen: true,

  // TODO: remove unused
  farmLandCropsGeoFeatures: [],
  farmNameTuples: [],
  selectedFarmName: "",
  defaultFarmName: "",
  cropTypes: [],
};

export const mapPageSlice = createSlice({
  name: MAP_PAGE_MODULE_NAME,
  initialState: initialMapPageState,
  reducers: {
    setIsLoadingGeoJsonAction(state, action: PayloadAction<boolean>): void {
      state.isLoadingGeoJson = action.payload;
    },
    setCenterAction(state, action: PayloadAction<LatLngExpression>): void {
      state.center = action.payload;
    },
    setZoomAction(state, action: PayloadAction<number>): void {
      state.zoom = action.payload;
    },
    setBoundsAction(state, action: PayloadAction<LatLngBounds>): void {
      const bounds = action.payload;

      if (!bounds.isValid()) {
        return;
      }

      state.bounds = bounds;
    },
    setGeoJsonAction(
      state,
      action: PayloadAction<GeoJSON<TArcgisMapCropPropsDto>>
    ): void {
      state.geoJson = action.payload;
    },
    setGeoJsonAlreadyFetchedForFarmSeasonAction(
      state,
      action: PayloadAction<string>
    ): void {
      state.geoJsonAlreadyFetchedForFarmSeason = action.payload;
    },
    setFarmLandCropsGeoFeaturesAction(state, action): void {
      state.farmLandCropsGeoFeatures = action.payload;
    },
    setSelectedCropNamesAction(state, action): void {
      state.selectedCropNames = action.payload;
    },
    setSelectedFarmNameAction(state, action): void {
      state.selectedFarmName = action.payload;
    },
    setFarmNameTuplesAction(state, action): void {
      state.farmNameTuples = action.payload;
    },
    setDefaultFarmNameAction(state, action): void {
      state.defaultFarmName = action.payload;
    },
    setCropTypesAction(state, action): void {
      state.cropTypes = action.payload;
    },
    setFarmLandDataAction(state, action: PayloadAction<TArcgisMapCropPropsDto[]>): void {
      state.farmLandData = action.payload;
    },
    setFarmFeaturesAction(
      state,
      action: PayloadAction<Feature<Geometry, TArcgisMapCropPropsDto>[] | undefined>
    ): void {
      state.farmFeatures = action.payload;
    },
    toggleDrawerOpenAction(state, _: PayloadAction): void {
      state.isDrawerOpen = !state.isDrawerOpen;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(logoutSuccess, (state) => {
        state.farmNameTuples = [];
      })
      .addCase(fetchGeoJsonAction.fulfilled, (state, action) => {
        if (
          state.geoJsonAlreadyFetchedForFarmSeason === geoJsonKey(action.meta.arg) ||
          state.geoJsonAlreadyFetchedForFarmSeason === ARCGIS_TOKEN_ERROR
        ) {
          state.isLoadingGeoJson = false;
        }
      })
      .addCase(fetchGeoJsonAction.pending, (state, action) => {
        state.isLoadingGeoJson = true;
        state.geoJsonAlreadyFetchedForFarmSeason = loadingKey(action.meta.requestId);
      })
      .addCase(fetchGeoJsonAction.rejected, (state, action) => {
        if (
          state.geoJsonAlreadyFetchedForFarmSeason === loadingKey(action.meta.requestId)
        ) {
          state.isLoadingGeoJson = false;
          state.geoJsonAlreadyFetchedForFarmSeason = NO_GEOJSON_REQUESTED;
        }
      });
  },
});

export const mapReducer = mapPageSlice.reducer;
export const {
  setIsLoadingGeoJsonAction,
  setFarmLandCropsGeoFeaturesAction,
  setSelectedCropNamesAction,
  setSelectedFarmNameAction,
  setFarmNameTuplesAction,
  setDefaultFarmNameAction,
  setCropTypesAction,
  setGeoJsonAction,
  setGeoJsonAlreadyFetchedForFarmSeasonAction,
  setZoomAction,
  setCenterAction,
  setBoundsAction,
  setFarmFeaturesAction,
  setFarmLandDataAction,
  toggleDrawerOpenAction,
} = mapPageSlice.actions;

export const fetchGeoJsonAction = createAsyncThunk<
  void,
  { farmId: string; seasonId: string },
  { state: RootState; signal: AbortSignal }
>(
  `${MAP_PAGE_MODULE_NAME}/fetchGeoJsonAction`,
  async ({ farmId, seasonId }, { dispatch, getState, signal, requestId }) => {
    const stringComparedByValue = geoJsonKey({ farmId, seasonId });
    const request = dispatch(
      mapPageApi.endpoints.fetchFarmLandGeometries.initiate(
        { farmId, seasonId },
        { subscribe: false, forceRefetch: true }
      )
    );
    let featureCollection;
    try {
      featureCollection = await request.unwrap();
    } finally {
      request.unsubscribe();
    }

    if (
      signal.aborted ||
      getState()[MAP_PAGE_MODULE_NAME].geoJsonAlreadyFetchedForFarmSeason !==
        loadingKey(requestId)
    ) {
      return;
    }

    if (isArcGisError(featureCollection)) {
      dispatch(setGeoJsonAlreadyFetchedForFarmSeasonAction(ARCGIS_TOKEN_ERROR));
      return;
    }

    const data: TArcgisMapCropPropsDto[] = firstEncountered(featureCollection.features);

    const geoJson = new GeoJSON(featureCollection);
    dispatch(setGeoJsonAction(geoJson));
    dispatch(setGeoJsonAlreadyFetchedForFarmSeasonAction(stringComparedByValue));

    const bounds: LatLngBounds = geoJson.getBounds();
    dispatch(setBoundsAction(bounds));

    try {
      dispatch(setCenterAction(bounds.getCenter()));
    } catch (error) {
      console.warn(error, `EMPTY_GEOMETRIES: cant find center of the map`);
    }

    dispatch(setFarmLandDataAction(data));

    const features: Feature<Geometry, TArcgisMapCropPropsDto>[] =
      featureCollection.features;
    dispatch(setFarmFeaturesAction(features));
  },
  {
    condition: (args, { getState }) =>
      getState()[MAP_PAGE_MODULE_NAME].geoJsonAlreadyFetchedForFarmSeason !==
      geoJsonKey(args),
  }
);

export const fetchAvailableFarmsAction = createAsyncThunk<
  number | null,
  void,
  { state: RootState }
>(
  `${MAP_PAGE_MODULE_NAME}/fetchAvailableFarmsAction`,
  async (_, { dispatch, getState }) => {
    const userId = getUserId(getState());
    if (!userId) {
      console.warn("fetchAvailableFarmsAction: /ims/me returned no userId");
      return 0;
    }

    const farmNameTuples = await dispatch(
      mapPageApi.endpoints.fetchAvailableFarms.initiate(userId, {
        subscribe: false,
        forceRefetch: true,
      })
    ).unwrap();

    if (getUserId(getState()) !== userId) {
      return null;
    }

    dispatch(setFarmNameTuplesAction(farmNameTuples));
    return farmNameTuples.length;
  }
);

function firstEncountered(
  features: Feature<Geometry, TArcgisMapCropPropsDto>[]
): TArcgisMapCropPropsDto[] {
  const mapCropByArcgisId = new Map<string | number, TArcgisMapCropPropsDto>();
  for (const x of features) {
    const featureId = x.id || x.properties.GDB_ARCHIVE_OID;
    if (!featureId) {
      continue;
    }
    if (mapCropByArcgisId.has(featureId)) {
      continue;
    }
    mapCropByArcgisId.set(featureId, x.properties);
  }

  return Array.from(mapCropByArcgisId.values());
}
